import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  CircularProgress,
  TableSortLabel,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  WalletCards,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  Trash2 as TrashIcon,
  FileText as FileTextIcon,
  Users as UsersIcon,
  CalendarDays as CalendarDaysIcon,
  HardDrive as HardDriveIcon,
  Mail as MailIcon,
  Phone as PhoneIcon,
  ExternalLink,
  HelpCircle
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }} elevation={0} variant="outlined">
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const monthsIt = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

function normalizeLower(value) {
  return String(value || '').trim().toLowerCase();
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const fixed = i === 0 ? 0 : 1;
  return `${v.toFixed(fixed)} ${units[i]}`;
}

export default function HrCedoliniCompensi() {
  const { user } = useAuth();
  const isAdmin = normalizeLower(user?.role) === 'admin' || normalizeLower(user?.role_name) === 'admin';

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [filterText, setFilterText] = useState('');
  const [filterAnno, setFilterAnno] = useState('');
  const [filterMese, setFilterMese] = useState('');

  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [filterDipendente, setFilterDipendente] = useState(null);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_creazione');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDipendente, setUploadDipendente] = useState(null);
  const [uploadAnno, setUploadAnno] = useState(String(new Date().getFullYear()));
  const [uploadMese, setUploadMese] = useState(String(new Date().getMonth() + 1));
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [openInNewTabLoading, setOpenInNewTabLoading] = useState(false);

  const yearOptions = useMemo(() => {
    const dipId = filterDipendente?.id ? String(filterDipendente.id) : '';
    const base = dipId ? (rows || []).filter((r) => String(r.dipendente_id) === dipId) : (rows || []);
    const set = new Set(base.map((r) => String(r.anno || '')).filter((v) => /^\d{4}$/.test(v)));
    return Array.from(set).sort((a, b) => String(b).localeCompare(String(a)));
  }, [filterDipendente?.id, rows]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/cedolini', { params: { limit: 500, offset: 0, sort: 'data_creazione', order: 'desc' } });
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
    } catch (error) {
      console.error('Errore caricamento cedolini:', error);
      toast.error(error.response?.data?.message || 'Errore caricamento cedolini');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDipendenti = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/dipendenti', { params: { limit: 300, offset: 0, sortBy: 'cognome', sortDir: 'ASC' } });
      const opts = (res.data?.items || []).map((d) => ({ id: String(d.id), label: `${d.cognome || ''} ${d.nome || ''}`.trim() }));
      setDipendentiOptions(opts);
    } catch (error) {
      console.error('Errore caricamento dipendenti:', error);
      setDipendentiOptions([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadRows();
    loadDipendenti();
  }, [loadDipendenti, loadRows]);

  useEffect(() => {
    if (!filterAnno) return;
    if (!yearOptions.includes(String(filterAnno))) {
      setFilterAnno('');
      setPage(0);
    }
  }, [filterAnno, yearOptions]);

  const filteredRows = useMemo(() => {
    const q = normalizeLower(filterText);
    const anno = String(filterAnno || '').trim();
    const mese = String(filterMese || '').trim();
    const dipId = filterDipendente?.id ? String(filterDipendente.id) : '';

    return (rows || []).filter((r) => {
      if (dipId && String(r.dipendente_id) !== dipId) return false;
      if (anno && String(r.anno) !== anno) return false;
      if (mese && String(r.mese) !== mese) return false;
      if (!q) return true;
      const dipLabel = `${r.dipendente_cognome || ''} ${r.dipendente_nome || ''}`.trim();
      const periodo = `${String(r.anno || '')}-${String(r.mese || '')}`;
      const filePath = String(r.file_path || '');
      return normalizeLower(dipLabel).includes(q) || normalizeLower(periodo).includes(q) || normalizeLower(filePath).includes(q);
    });
  }, [filterAnno, filterDipendente?.id, filterMese, filterText, rows]);

  const sortedRows = useMemo(() => {
    const dir = order === 'asc' ? 1 : -1;
    const key = String(orderBy || '');

    return [...filteredRows].sort((a, b) => {
      let av = a?.[key];
      let bv = b?.[key];

      if (key === 'dipendente') {
        av = `${a?.dipendente_cognome || ''} ${a?.dipendente_nome || ''}`.trim();
        bv = `${b?.dipendente_cognome || ''} ${b?.dipendente_nome || ''}`.trim();
      }

      if (key === 'periodo') {
        av = `${String(a?.anno || '').padStart(4, '0')}-${String(a?.mese || '').padStart(2, '0')}`;
        bv = `${String(b?.anno || '').padStart(4, '0')}-${String(b?.mese || '').padStart(2, '0')}`;
      }

      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;

      const as = String(av);
      const bs = String(bv);

      if (key.includes('data_')) {
        const ad = new Date(as);
        const bd = new Date(bs);
        if (!Number.isNaN(ad.getTime()) && !Number.isNaN(bd.getTime())) {
          return (ad.getTime() - bd.getTime()) * dir;
        }
      }

      const cmp = as.localeCompare(bs, 'it', { sensitivity: 'base' });
      return cmp * dir;
    });
  }, [filteredRows, order, orderBy]);

  const pagedRows = useMemo(
    () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedRows]
  );

  const kpi = useMemo(() => {
    const total = filteredRows.length;
    const uniqueDip = new Set(filteredRows.map((r) => String(r.dipendente_id || '')).filter(Boolean)).size;
    const totalSize = filteredRows.reduce((acc, r) => acc + Number(r.dimensione_file || 0), 0);
    const last = filteredRows
      .map((r) => String(r.data_aggiornamento || r.data_creazione || ''))
      .filter(Boolean)
      .sort((a, b) => String(b).localeCompare(String(a)))[0] || '';
    return { total, uniqueDip, totalSize, last };
  }, [filteredRows]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const revokePreviewUrl = () => {
    if (previewUrl && String(previewUrl).startsWith('blob:')) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewRow(null);
    revokePreviewUrl();
  };

  const openPreview = async (row) => {
    if (!row?.id) return;
    try {
      const fileName = String(row.file_path || '').split('/').pop() || 'PDF';
      const monthLabel = monthsIt.find((m) => Number(m.value) === Number(row.mese))?.label || String(row.mese || '');
      setPreviewRow({ ...row, file_name: fileName, periodo_label: `${monthLabel} ${row.anno}`.trim() });
      setPreviewOpen(true);
      revokePreviewUrl();
      setPreviewLoading(true);
      const res = await api.get(`/hr/cedolini/${row.id}/preview-url`);
      const url = res.data?.url;
      if (!url) throw new Error('URL non ricevuto');
      setPreviewUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore apertura preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenPreviewInNewTab = async () => {
    if (!previewRow?.id) return;

    if (previewUrl) {
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      setOpenInNewTabLoading(true);
      const res = await api.get(`/hr/cedolini/${previewRow.id}/preview-url`);
      const url = res.data?.url;
      if (!url) throw new Error('URL non ricevuto');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore apertura preview');
    } finally {
      setOpenInNewTabLoading(false);
    }
  };

  const downloadFile = async (row) => {
    try {
      const res = await api.get(`/hr/cedolini/${row.id}/download-url`);
      const url = res.data?.url;
      if (!url) throw new Error('URL non ricevuto');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore download');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/hr/cedolini/${deleteTarget.id}`);
      toast.success('Cedolino eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadRows();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Errore eliminazione cedolino');
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadDipendente(null);
    setUploadAnno(String(new Date().getFullYear()));
    setUploadMese(String(new Date().getMonth() + 1));
    setUploadFile(null);
  };

  const openUploadDialog = () => {
    resetUploadForm();
    setUploadDialogOpen(true);
  };

  const pickFile = () => {
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleFileSelected = (file) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File troppo grande: max 5MB');
      return;
    }
    const mime = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    if (!mime.includes('pdf') && !name.endsWith('.pdf')) {
      toast.error('Formato non valido: caricare un PDF');
      return;
    }
    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadDipendente?.id) {
      toast.error('Seleziona un dipendente');
      return;
    }
    const anno = String(uploadAnno || '').trim();
    const mese = String(uploadMese || '').trim();
    if (!/^\d{4}$/.test(anno)) {
      toast.error('Anno non valido');
      return;
    }
    const meseNum = Number(mese);
    if (!Number.isFinite(meseNum) || meseNum < 1 || meseNum > 12) {
      toast.error('Mese non valido');
      return;
    }
    if (!uploadFile) {
      toast.error('Seleziona un PDF');
      return;
    }

    try {
      setUploading(true);
      const form = new FormData();
      form.append('dipendente_id', String(uploadDipendente.id));
      form.append('anno', anno);
      form.append('mese', String(meseNum));
      form.append('file', uploadFile);

      await api.post('/hr/cedolini/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Cedolino caricato');
      setUploadDialogOpen(false);
      loadRows();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore upload cedolino');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#0ea5e91A',
              color: '#0ea5e9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <WalletCards size={20} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Cedolini/Compensi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione cedolini (PDF) per dipendente e periodo di competenza.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {isAdmin && (
            <Button variant="contained" onClick={openUploadDialog} startIcon={<UploadIcon size={16} />}>
              Carica cedolino
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Cedolini" value={kpi.total} icon={FileTextIcon} color="#0ea5e9" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Dipendenti" value={kpi.uniqueDip} icon={UsersIcon} color="#6366f1" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Totale dimensione" value={formatBytes(kpi.totalSize)} icon={HardDriveIcon} color="#22c55e" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Ultimo upload" value={kpi.last ? new Date(kpi.last).toLocaleDateString('it-IT') : '—'} icon={CalendarDaysIcon} color="#f59e0b" />
        </Grid>
      </Grid>

      <StyledCard>
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder={isAdmin ? 'Cerca (dipendente, periodo, file)' : 'Cerca (periodo, file)'}
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={4}>
                <Autocomplete
                  size="small"
                  options={dipendentiOptions}
                  value={filterDipendente}
                  onChange={(_, val) => {
                    setFilterDipendente(val);
                    setPage(0);
                  }}
                  isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                  getOptionLabel={(o) => o?.label || ''}
                  renderInput={(params) => <TextField {...params} label="Dipendente" />}
                />
              </Grid>
            )}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Anno</InputLabel>
                <Select
                  label="Anno"
                  value={filterAnno}
                  onChange={(e) => {
                    setFilterAnno(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mese</InputLabel>
                <Select
                  label="Mese"
                  value={filterMese}
                  onChange={(e) => {
                    setFilterMese(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {monthsIt.map((m) => (
                    <MenuItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {isAdmin && (
                      <TableCell>
                        <TableSortLabel active={orderBy === 'dipendente'} direction={order} onClick={() => handleSort('dipendente')}>
                          Dipendente
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell>
                      <TableSortLabel active={orderBy === 'periodo'} direction={order} onClick={() => handleSort('periodo')}>
                        Periodo
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>File</TableCell>
                    <TableCell align="right">
                      <TableSortLabel active={orderBy === 'dimensione_file'} direction={order} onClick={() => handleSort('dimensione_file')}>
                        Dimensione
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={orderBy === 'data_creazione'} direction={order} onClick={() => handleSort('data_creazione')}>
                        Caricato il
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Caricato da</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row) => {
                    const dipLabel = `${row.dipendente_cognome || ''} ${row.dipendente_nome || ''}`.trim();
                    const dipEmail = String(row.dipendente_email || '').trim();
                    const dipPhone = String(row.dipendente_cellulare_aziendale || row.dipendente_cellulare_privato || row.dipendente_telefono || '').trim();
                    const monthLabel = monthsIt.find((m) => Number(m.value) === Number(row.mese))?.label || String(row.mese);
                    const periodo = `${monthLabel} ${row.anno}`;
                    const fileName = String(row.file_path || '').split('/').pop() || 'PDF';
                    const fileShort = fileName.length > 32 ? `${fileName.slice(0, 29)}…` : fileName;
                    return (
                      <TableRow key={row.id} hover>
                        {isAdmin && (
                          <TableCell>
                            <Box sx={{ display: 'grid', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {dipLabel || '-'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                                <MailIcon size={14} />
                                <Typography variant="body2" sx={{ fontSize: 13 }}>
                                  {dipEmail || '-'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                                <PhoneIcon size={14} />
                                <Typography variant="body2" sx={{ fontSize: 13 }}>
                                  {dipPhone || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        )}
                        <TableCell>{periodo}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1,
                                bgcolor: '#e5e7eb',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <FileTextIcon size={16} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {fileShort}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatBytes(row.dimensione_file)}</TableCell>
                        <TableCell>{row.data_creazione ? new Date(row.data_creazione).toLocaleDateString('it-IT') : '-'}</TableCell>
                        <TableCell>{row.caricato_da_nome || '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openPreview(row)}>
                            <EyeIcon size={16} />
                          </IconButton>
                          <IconButton size="small" onClick={() => downloadFile(row)}>
                            <DownloadIcon size={16} />
                          </IconButton>
                          {isAdmin && (
                            <IconButton size="small" color="error" onClick={() => openDeleteDialog(row)}>
                              <TrashIcon size={16} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {pagedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          Nessun cedolino trovato
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
        <TablePagination
          component="div"
          count={sortedRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Righe per pagina"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`}
        />
      </StyledCard>

      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={handleClosePreview}
        hideBackdrop
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 520 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Anteprima documento
              </Typography>
              <Tooltip arrow placement="bottom" title="Usa “Apri in nuova scheda” per aprire a schermo intero.">
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <HelpCircle size={16} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExternalLink size={14} />}
                onClick={handleOpenPreviewInNewTab}
                disabled={!previewRow || openInNewTabLoading}
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 2,
                  borderRadius: '12px',
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                {openInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleClosePreview}
                sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
              >
                Chiudi
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              {previewLoading && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!previewLoading && previewRow && previewUrl && (
                <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                  <iframe title={previewRow.file_name || 'Anteprima documento'} src={previewUrl} style={{ border: 0, width: '100%', height: '100%' }} />
                </Box>
              )}

              {!previewLoading && previewRow && !previewUrl && (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Anteprima non disponibile.
                  </Typography>
                </Box>
              )}
            </Box>

            {previewRow && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dettagli
                </Typography>
                <Typography variant="body2">Nome: {previewRow.file_name || '-'}</Typography>
                <Typography variant="body2">Tipo: Cedolino/Compenso</Typography>
                <Typography variant="body2">Formato: PDF</Typography>
                <Typography variant="body2">Dimensione: {formatBytes(previewRow.dimensione_file)}</Typography>
                {isAdmin && (
                  <Typography variant="body2">
                    Dipendente: {`${previewRow.dipendente_cognome || ''} ${previewRow.dipendente_nome || ''}`.trim() || '-'}
                  </Typography>
                )}
                <Typography variant="body2">Periodo: {previewRow.periodo_label || '-'}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '2px' } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Carica cedolino
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Associa un PDF a dipendente e periodo di competenza.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Autocomplete
              size="small"
              options={dipendentiOptions}
              value={uploadDipendente}
              onChange={(_, val) => setUploadDipendente(val)}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              getOptionLabel={(o) => o?.label || ''}
              renderInput={(params) => <TextField {...params} label="Dipendente" placeholder="Seleziona dipendente" />}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                size="small"
                label="Anno"
                value={uploadAnno}
                onChange={(e) => setUploadAnno(e.target.value)}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Mese</InputLabel>
                <Select label="Mese" value={uploadMese} onChange={(e) => setUploadMese(e.target.value)}>
                  {monthsIt.map((m) => (
                    <MenuItem key={m.value} value={String(m.value)}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>
                File PDF
              </Typography>
              <Box
                onClick={pickFile}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const f = e.dataTransfer?.files?.[0] || null;
                  handleFileSelected(f);
                }}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: '2px',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: 'background.paper'
                }}
              >
                <UploadIcon size={22} />
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  Trascina il PDF qui o scegli file
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Solo PDF, max 5 MB
                </Typography>
                {uploadFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {uploadFile.name}
                  </Typography>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  handleFileSelected(f);
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadDialogOpen(false)} variant="outlined" disabled={uploading}>
            Annulla
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
            sx={(theme) => ({
              bgcolor: theme.palette.mode === 'dark' ? '#f9fafb' : '#111827',
              color: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
              '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#e5e7eb' : '#0f172a' }
            })}
          >
            {uploading ? 'Caricamento...' : 'Carica cedolino'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '2px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Elimina cedolino</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Stai per eliminare il cedolino selezionato. Questa operazione è irreversibile.
            </Typography>
            {deleteTarget && (
              <Box sx={{ mt: 0.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '2px' }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {`${String(deleteTarget.dipendente_cognome || '').trim()} ${String(deleteTarget.dipendente_nome || '').trim()}`.trim() || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {`${monthsIt.find((m) => Number(m.value) === Number(deleteTarget.mese))?.label || deleteTarget.mese} ${deleteTarget.anno}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  File:{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                      }}
                    >
                      {String(deleteTarget.file_path || '').split('/').pop() || 'PDF'}
                    </Box>
                  </Box>
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              Il PDF verrà rimosso dallo storage e non sarà più disponibile per download o anteprima.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" disabled={deleteLoading}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
